// @flow
import filter from 'lodash/filter'
import isEmpty from 'lodash/isEmpty'
import useChangeResponseCustomer from 'pages/response/hooks/useChangeResponseCustomer'
import useResponseArchive from 'pages/response/hooks/useResponseArchive'
import useResponseDelete from 'pages/response/hooks/useResponseDelete'
import useResponseDownload from 'pages/response/hooks/useResponseDownload'
import useResponseInternal from 'pages/response/hooks/useResponseInternal'
import useResponseManualSubmit from 'pages/response/hooks/useResponseManualSubmit'
import useResponseOfficeUse from 'pages/response/hooks/useResponseOfficeUse'
import useResponseSendReminder from 'pages/response/hooks/useResponseSendReminder'
import useResponseUnarchive from 'pages/response/hooks/useResponseUnarchive'
import * as React from 'react'
import type { Message } from 'types/message'
import type { PacketResponse } from 'types/packet-response'
import type { Response } from 'types/response'

import useResponseQueue from './useResponseQueue'

type Values = {
  buttonList: Array<{
    title: string,
    onClick: () => void,
    loading?: boolean,
  }>,
  isSendingReminder: boolean,
  setIsSendingReminder: (isSendingReminder: boolean) => void,
}

type Params = {
  response?: ?Response,
  setResponse?: (Response) => void,
  packetResponse?: ?PacketResponse,
  setPacketResponse?: (PacketResponse) => void,
  message?: ?Message,
  fetchData?: () => Promise<void>,
}

function useResponseControls({
  response,
  setResponse,
  packetResponse,
  message,
  setPacketResponse,
  fetchData,
}: Params): Values {
  const isPartOfPacket = response?.packet_response

  const isInternalForm = response?.form?.is_internal_form
  const isSubmitted = response?.is_submitted || packetResponse?.is_submitted
  const [
    showSendRemidner,
    isSendingReminder,
    setIsSendingReminder,
    sendReminder,
  ] = useResponseSendReminder(response, packetResponse, message)

  const responseQueue = useResponseQueue(
    response,
    packetResponse,
    setResponse,
    setPacketResponse
  )

  const responseDownload = useResponseDownload(response, packetResponse)

  const [hasOfficeUse, responseOfficeUse] = useResponseOfficeUse(
    response,
    packetResponse,
    message
  )

  const [showInternal, responseInternal] = useResponseInternal(
    response,
    message
  )

  const [isArchived, responseArchive] = useResponseArchive(
    response,
    setResponse,
    packetResponse,
    setPacketResponse
  )

  const responseUnarchive = useResponseUnarchive(
    response,
    setResponse,
    packetResponse,
    setPacketResponse
  )

  const responseDelete = useResponseDelete(response, packetResponse, message)

  const [isManuallySubmitted, responseManualSubmit] = useResponseManualSubmit(
    response,
    setResponse,
    packetResponse,
    setPacketResponse
  )

  const [ChangeResponseCustomerModal, changeResponseCustomer] =
    useChangeResponseCustomer({
      fetchData,
      isAnonymous: response?.customer?.is_anonymous,
    })

  const isInternal = message?.message_type === 'internal'

  const buttonList = React.useMemo(() => {
    const btnList = [
      showSendRemidner && sendReminder,
      !isSubmitted && !isInternalForm && responseQueue,
      showInternal && responseInternal,
      !isInternal && hasOfficeUse && responseOfficeUse,
      isArchived && responseUnarchive,
      !isArchived && responseArchive,
      isSubmitted && !isPartOfPacket && changeResponseCustomer,
      responseDelete,
    ]

    if (isSubmitted) {
      if (showInternal) {
        btnList.splice(3, 0, responseDownload)
      } else {
        btnList.splice(0, 0, responseDownload)
      }
    } else {
      btnList.splice(5, 0, responseDownload)
    }

    if (!isManuallySubmitted && !isInternal && !response?.packet_response) {
      btnList.splice(2, 1, responseManualSubmit)
    }
    return filter(btnList, (b) => !isEmpty(b))
  }, [
    showSendRemidner,
    sendReminder,
    isSubmitted,
    isInternalForm,
    responseQueue,
    showInternal,
    responseInternal,
    isInternal,
    hasOfficeUse,
    responseOfficeUse,
    isArchived,
    responseUnarchive,
    responseArchive,
    isPartOfPacket,
    changeResponseCustomer,
    responseDelete,
    isManuallySubmitted,
    response?.packet_response,
    responseDownload,
    responseManualSubmit,
  ])

  return {
    buttonList,
    isSendingReminder,
    setIsSendingReminder,
    ChangeResponseCustomerModal,
  }
}

export default useResponseControls
